import { useColorMode } from '@vueuse/core'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import i18n from '@/libs/i18n'

export interface LoggedInUserData {
    id:       string
    name:     string
    username: string
    role:     string
}

// Decodes the JWT payload client-side to read id/name/username/role — these
// claims are already visible to the browser inside the token itself (base64,
// not encrypted), so this reveals nothing the client didn't already have; it
// just avoids a round-trip to a "/me" endpoint the API doesn't expose.
const decodeJWT = (token: string): LoggedInUserData | undefined => {
    try {
        const payload = token.split('.')[1]
        if (!payload) return undefined

        const json = decodeURIComponent(
            atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
                .split('')
                .map(c => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
                .join('')
        )

        const claims = JSON.parse(json)

        return {
            id:       claims.id,
            name:     claims.name,
            username: claims.username,
            role:     claims.role,
        }
    } catch {
        return undefined
    }
}

export const useAppStore = defineStore('app', () => {
    const JWT         = ref(localStorage.getItem('JWT') || '')
    const locale      = ref(localStorage.getItem('locale') || 'sl')
    const windowWidth = ref(window.innerWidth)
    const loaderActive = ref(0)
    const userData    = ref<LoggedInUserData | undefined>(JWT.value ? decodeJWT(JWT.value) : undefined)
    const sidebarOpen = ref(false)

    window.addEventListener('resize', () => { windowWidth.value = window.innerWidth })

    // Wraps the SAME state @nuxt/ui's vue plugin already manages via useDark() —
    // never a second source of truth. Values: 'auto' | 'light' | 'dark'
    // (presented as System / Light / Dark, default System). See theme.md.
    const colorMode = useColorMode({ storageKey: 'vueuse-color-scheme', emitAuto: true })

    const getJWT     = () => JWT.value
    const isLoggedIn = () => !!JWT.value
    const isLoaderActive = () => loaderActive.value > 0
    const isMobile   = () => windowWidth.value < 1024
    const isAdmin    = () => userData.value?.role === 'admin'

    const toggleSidebar = () => sidebarOpen.value = !sidebarOpen.value
    const closeSidebar  = () => sidebarOpen.value = false

    // Sets a JWT cookie so that API requests for images/PDFs loaded via <img src> or
    // <a href> are authenticated — browsers cannot set Authorization headers for those.
    const setJWTCookie = (jwt: string) => {
        if (jwt) {
            document.cookie = `JWT=${jwt}; path=/; SameSite=Lax`
        } else {
            document.cookie = 'JWT=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax'
        }
    }

    // Restore cookie on page load if JWT is already in localStorage
    setJWTCookie(JWT.value)

    const setLogin = (jwt: string) => {
        localStorage.setItem('JWT', jwt)
        JWT.value = jwt
        userData.value = decodeJWT(jwt)
        setJWTCookie(jwt)
    }

    const setLogout = () => {
        localStorage.removeItem('JWT')
        JWT.value = ''
        userData.value = undefined
        setJWTCookie('')
    }

    const startLoading = () => loaderActive.value++
    const stopLoading  = () => loaderActive.value = Math.max(0, loaderActive.value - 1)

    const changeLocale = (newLocale: string) => {
        locale.value = newLocale
        localStorage.setItem('locale', newLocale)
        ;(i18n.global.locale as { value: string }).value = newLocale
    }

    return {
        JWT, locale, userData, loaderActive, colorMode, sidebarOpen,
        getJWT, isLoggedIn, isLoaderActive, isMobile, isAdmin,
        setLogin, setLogout, startLoading, stopLoading, changeLocale,
        toggleSidebar, closeSidebar,
    }
})
